import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateSchoolResponse, ModuleDto, PlanKey } from '@eskoolhub/types';
import { ModuleBoard, moduleSummary, type BoardModule } from '../components/ModuleBoard';
import { useToast } from '../components/Toasts';
import { Modal } from '../components/ui';
import { api, ApiRequestError } from '../lib/api';
import { formatINR, formatRate } from '../lib/format';
import { PricingFields, draftToLumpSumPaise, draftToPaise, emptyPricingDraft, type PricingDraft } from './PricingFields';

interface PlanSummary {
  id: string;
  key: PlanKey;
  name: string;
  tagline: string;
  pricePerStudentPerYearPaise: number | null;
  pricePerStudentPerMonthPaise: number | null;
  studentLimit: number | null;
  defaultModules: string[];
}

const BOARDS = ['CBSE', 'ICSE', 'State Board', 'IB', 'IGCSE'];

/**
 * A03 — onboard a school. Two steps: the school's details, then the feature
 * switchboard pre-ticked from the chosen plan and adjustable per school.
 */
export function OnboardSchoolModal({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<1 | 2>(1);
  const [form, setForm] = useState({
    name: '',
    city: '',
    state: 'Maharashtra',
    board: 'CBSE',
    planKey: 'GROWTH' as PlanKey,
    billingCycle: 'ANNUAL' as 'ANNUAL' | 'MONTHLY',
    billableStudents: '',
    adminName: '',
    adminEmail: '',
    adminPhone: '+91',
    trialDays: '14',
    // The platform green, as a starting point rather than a default nobody
    // revisits: a school that never changes it still looks deliberate, and one
    // that does has somewhere obvious to do it.
    primaryColor: '#1FA97A',
    // A single pasted pair rather than two number fields — the office already
    // has a map: right-click the school in Google Maps and the numbers at the
    // top of the menu are exactly this, copied. Optional: the school can set
    // or change this later from its own Transport settings.
    location: '',
  });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [pricing, setPricing] = useState<PricingDraft>(emptyPricingDraft);
  const [touchedModules, setTouchedModules] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const { data: plansData } = useQuery({
    queryKey: ['plans'],
    queryFn: () => api.get<{ plans: PlanSummary[] }>('/api/billing/plans'),
  });

  const { data: catalogData } = useQuery({
    queryKey: ['module-catalog'],
    queryFn: () => api.get<{ modules: ModuleDto[] }>('/api/modules/catalog'),
  });

  const plan = plansData?.plans.find((p) => p.key === form.planKey);

  // Changing plan re-seeds the selection — unless the operator has already
  // hand-picked modules, in which case their choices are kept.
  useEffect(() => {
    if (!plan || touchedModules) return;
    setSelected(new Set(plan.defaultModules));
  }, [plan, touchedModules]);

  const modules: BoardModule[] = useMemo(() => {
    if (!catalogData) return [];
    const defaults = new Set(plan?.defaultModules ?? []);
    return catalogData.modules.map((m) => ({
      key: m.key,
      name: m.name,
      description: m.description,
      category: m.category,
      isCore: m.isCore,
      requires: m.requires,
      planDefault: m.isCore || defaults.has(m.key),
    }));
  }, [catalogData, plan]);

  const students = Number(form.billableStudents) || 0;
  const customRatePaise = draftToPaise(pricing);
  const lumpSumPaise = draftToLumpSumPaise(pricing);
  const effectiveMonthlyPaise = customRatePaise ?? plan?.pricePerStudentPerMonthPaise ?? null;
  const annualPaise =
    lumpSumPaise !== null ? lumpSumPaise : effectiveMonthlyPaise === null ? 0 : effectiveMonthlyPaise * 12 * students;

  // ENTERPRISE has no list price, so a rate or a lump sum has to be given before we can bill.
  const needsRate = plan?.pricePerStudentPerMonthPaise === null && customRatePaise === null && lumpSumPaise === null;

  // Accepts "22.7240, 75.9028" exactly as Google Maps hands it over, and the
  // same thing with a space or a slash between. Blank is a valid answer too —
  // the school can set this later — so only a non-blank, malformed pair is an
  // error.
  const locationParts = form.location.trim().split(/[,\s/]+/).filter(Boolean).map(Number);
  const locationValid =
    form.location.trim() === '' ||
    (locationParts.length === 2 &&
      Number.isFinite(locationParts[0]) &&
      Number.isFinite(locationParts[1]) &&
      Math.abs(locationParts[0]!) <= 90 &&
      Math.abs(locationParts[1]!) <= 180);

  const create = useMutation({
    mutationFn: () =>
      api.post<CreateSchoolResponse>('/api/schools', {
        name: form.name.trim(),
        city: form.city.trim(),
        state: form.state.trim(),
        board: form.board,
        planKey: form.planKey,
        billingCycle: form.billingCycle,
        billableStudents: students,
        adminName: form.adminName.trim(),
        adminEmail: form.adminEmail.trim(),
        adminPhone: form.adminPhone.trim(),
        trialDays: Number(form.trialDays) || 0,
        modules: [...selected],
        primaryColor: form.primaryColor,
        ...(locationParts.length === 2
          ? { latitude: locationParts[0], longitude: locationParts[1] }
          : {}),
        pricePerStudentPerMonthPaise: customRatePaise,
        lumpSumPerYearPaise: lumpSumPaise,
        pricingNote: pricing.note.trim() || undefined,
      }),
    onSuccess: (result) => {
      toast.push(`${result.school.name} onboarded · invite sent to ${result.invite.email}`, 'success');
      void queryClient.invalidateQueries();
      onClose();
      navigate(`/schools/${result.school.id}`);
    },
    onError: (error) => {
      if (error instanceof ApiRequestError) {
        setFieldErrors(error.fields ?? {});
        setFormError(error.fields ? null : error.message);
        if (error.fields) setStep(1);
      } else {
        setFormError('Could not create the school');
      }
    },
  });

  function update<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((current) => ({ ...current, [key]: value }));
    setFieldErrors((current) => {
      if (!(key in current)) return current;
      const next = { ...current };
      delete next[key as string];
      return next;
    });
  }

  const field = (key: string) => fieldErrors[key];

  return (
    <Modal
      title="Onboard a school"
      onClose={onClose}
      wide={step === 2}
      footer={
        <>
          {step === 2 ? (
            <button className="btn btn--ghost" onClick={() => setStep(1)} disabled={create.isPending}>
              Back
            </button>
          ) : (
            <button className="btn btn--ghost" onClick={onClose}>
              Cancel
            </button>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 14 }}>
            {step === 2 && plan ? (
              <span className="caption">
                {moduleSummary(modules, selected)}
                {lumpSumPaise !== null || (effectiveMonthlyPaise !== null && students > 0)
                  ? ` · ${formatINR(annualPaise)}/year`
                  : ''}
              </span>
            ) : null}
            {step === 1 ? (
              <button className="btn" onClick={() => setStep(2)} disabled={needsRate || !locationValid}>
                {needsRate ? 'Set a rate to continue' : 'Next · choose features'}
              </button>
            ) : (
              <button className="btn" onClick={() => create.mutate()} disabled={create.isPending}>
                {create.isPending ? 'Creating…' : 'Create school & invite admin'}
              </button>
            )}
          </div>
        </>
      }
    >
      {formError ? <div className="banner banner--error">{formError}</div> : null}

      {step === 1 ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <label className="field" style={{ gridColumn: 'span 2' }}>
            <span>School name</span>
            <input value={form.name} onChange={(e) => update('name', e.target.value)} placeholder="Lakeside Academy" />
            {field('name') ? <span className="field__error">{field('name')}</span> : null}
          </label>

          <label className="field">
            <span>City</span>
            <input value={form.city} onChange={(e) => update('city', e.target.value)} placeholder="Nashik" />
            {field('city') ? <span className="field__error">{field('city')}</span> : null}
          </label>

          <label className="field">
            <span>Board</span>
            <select value={form.board} onChange={(e) => update('board', e.target.value)}>
              {BOARDS.map((board) => (
                <option key={board}>{board}</option>
              ))}
            </select>
          </label>

          <label className="field">
            <span>Admin name</span>
            <input
              value={form.adminName}
              onChange={(e) => update('adminName', e.target.value)}
              placeholder="Vikram Deshmukh"
            />
            {field('adminName') ? <span className="field__error">{field('adminName')}</span> : null}
          </label>

          <label className="field">
            <span>Admin email</span>
            <input
              type="email"
              value={form.adminEmail}
              onChange={(e) => update('adminEmail', e.target.value)}
              placeholder="vikram@lakeside.edu"
            />
            {field('adminEmail') ? <span className="field__error">{field('adminEmail')}</span> : null}
          </label>

          <label className="field">
            <span>Admin phone</span>
            <input
              value={form.adminPhone}
              onChange={(e) => update('adminPhone', e.target.value)}
              placeholder="+919856412300"
            />
            {field('adminPhone') ? <span className="field__error">{field('adminPhone')}</span> : null}
          </label>

          <label className="field">
            <span>Students (billable)</span>
            <input
              inputMode="numeric"
              value={form.billableStudents}
              onChange={(e) => update('billableStudents', e.target.value.replace(/\D/g, ''))}
              placeholder="540"
            />
            {field('billableStudents') ? <span className="field__error">{field('billableStudents')}</span> : null}
          </label>

          <div className="field" style={{ gridColumn: 'span 2' }}>
            <span>Brand colour</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <input
                type="color"
                aria-label="Brand colour"
                value={form.primaryColor}
                onChange={(e) => update('primaryColor', e.target.value)}
                style={{
                  width: 44,
                  height: 36,
                  padding: 2,
                  border: '1px solid var(--rule)',
                  borderRadius: 8,
                  background: 'var(--card)',
                  cursor: 'pointer',
                  flex: 'none',
                }}
              />
              <input
                value={form.primaryColor}
                onChange={(e) => update('primaryColor', e.target.value)}
                placeholder="#1FA97A"
                spellCheck={false}
                style={{ fontFamily: 'var(--mono, ui-monospace, monospace)', flex: 1 }}
              />
            </div>
            <span className="field__hint">
              Used on the school's login screen and through the parent app.
            </span>
            {field('primaryColor') ? <span className="field__error">{field('primaryColor')}</span> : null}
          </div>

          <div className="field" style={{ gridColumn: 'span 2' }}>
            <span>Logo</span>
            <div className="caption" style={{ margin: 0 }}>
              Uploaded from the school's own page — creating it takes you straight there. Shows
              its initials until then.
            </div>
          </div>

          <label className="field" style={{ gridColumn: 'span 2' }}>
            <span>School location <span style={{ opacity: 0.6 }}>· optional</span></span>
            <input
              value={form.location}
              onChange={(e) => update('location', e.target.value)}
              placeholder="22.7240, 75.9028"
              spellCheck={false}
              style={{ fontFamily: 'var(--mono, ui-monospace, monospace)' }}
            />
            <span className="field__hint">
              In Google Maps, right-click the school and click the numbers at the top
              of the menu — that copies them. Paste them here. Also settable later from
              the school's own Transport settings.
            </span>
            {!locationValid ? (
              <span className="field__error">
                Paste both numbers, e.g. 22.7240, 75.9028 — or leave it blank
              </span>
            ) : field('latitude') ? (
              <span className="field__error">{field('latitude')}</span>
            ) : null}
          </label>

          <label className="field" style={{ gridColumn: 'span 2' }}>
            <span>Plan</span>
            <div className="segmented">
              {(plansData?.plans ?? []).map((option) => (
                <button
                  key={option.key}
                  type="button"
                  aria-pressed={form.planKey === option.key}
                  onClick={() => update('planKey', option.key)}
                >
                  {option.name}
                </button>
              ))}
            </div>
            {plan ? (
              <span className="caption">
                {plan.tagline}
                {plan.pricePerStudentPerMonthPaise
                  ? ` · list ${formatRate(plan.pricePerStudentPerMonthPaise)}/student/month`
                  : ' · priced per deal'}
              </span>
            ) : null}
          </label>

          <label className="field">
            <span>Billing cycle</span>
            <div className="segmented">
              {(['ANNUAL', 'MONTHLY'] as const).map((cycle) => (
                <button
                  key={cycle}
                  type="button"
                  aria-pressed={form.billingCycle === cycle}
                  onClick={() => update('billingCycle', cycle)}
                >
                  {cycle === 'ANNUAL' ? 'Annual' : 'Monthly'}
                </button>
              ))}
            </div>
          </label>

          <label className="field">
            <span>Trial length (days)</span>
            <input
              inputMode="numeric"
              value={form.trialDays}
              onChange={(e) => update('trialDays', e.target.value.replace(/\D/g, ''))}
            />
            <span className="caption">0 = go live immediately, no trial</span>
          </label>

          <div style={{ gridColumn: 'span 2' }}>
            <PricingFields
              draft={pricing}
              onChange={setPricing}
              planName={plan?.name ?? 'This plan'}
              planListMonthlyPaise={plan?.pricePerStudentPerMonthPaise ?? null}
              students={students}
              billingCycle={form.billingCycle}
            />
            {fieldErrors.pricePerStudentPerMonthPaise || fieldErrors.lumpSumPerYearPaise ? (
              <span className="field__error">
                {fieldErrors.pricePerStudentPerMonthPaise ?? fieldErrors.lumpSumPerYearPaise}
              </span>
            ) : null}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="banner" style={{ background: 'var(--paper)', color: 'var(--ink-soft)' }}>
            Pre-ticked from the <strong style={{ margin: '0 4px' }}>{plan?.name}</strong> plan. Switch anything on
            or off for this school — the choice is saved against the school, not the plan.
          </div>
          <ModuleBoard modules={modules} selected={selected} onChange={(next) => { setTouchedModules(true); setSelected(next); }} />
        </div>
      )}
    </Modal>
  );
}
